import { useCallback, useEffect, useMemo, useRef } from 'react'
import findIndex from 'lodash/findIndex'
import { Swiper as SwiperClass } from 'swiper'
import { Swiper as ReactSwiper, SwiperSlide } from 'swiper/react'
import { UserDocumentTemplate } from '@rio/types'

import { formatImageUrl } from 'builder/utils/formatImageUrl'
import { TemplateCategories, TemplateChangeHandler } from 'builder/modules/resumeEditor'

import {
  TemplateSelectionMobileContainer,
  BadgeWrapper,
  TemplateContainer,
  PlanBadges,
  FormatBadges,
} from 'builder/components/DocumentCustomizationModal/components/ResumeSidebar/components/TemplateSelectionMobile/styles'
import {
  TemplateMobile,
  TemplatesMobile as TemplatesMobileComponent,
  TemplatePreviewMobile,
} from './styles'

type Props = {
  templates: UserDocumentTemplate[]
  selectedTemplates: string[]
  selectedCategory: `${TemplateCategories}`
  currentTemplate?: UserDocumentTemplate
  updateTemplate: TemplateChangeHandler
}

export const TemplatesMobile = (props: Props) => {
  const { templates, updateTemplate, currentTemplate, selectedTemplates, selectedCategory } = props
  const templateListRef = useRef<HTMLDivElement | null>(null)
  const templatesGallery = useRef<SwiperClass | null>(null)

  const filteredTemplates = useMemo(() => {
    return templates.filter(template => {
      if (
        selectedTemplates.length &&
        selectedCategory !== TemplateCategories.all &&
        !selectedTemplates.includes(template.id)
      ) {
        return false
      }

      if (selectedCategory === TemplateCategories.free && template.premium) {
        return false
      }

      return true
    })
  }, [templates, selectedTemplates, selectedCategory])

  const initialSlide = useMemo(() => {
    const idx = findIndex(filteredTemplates, { id: currentTemplate?.id })
    return idx < 0 ? 0 : idx
  }, [currentTemplate, filteredTemplates])

  const handleTemplateChange = useCallback(
    (swiper: SwiperClass) => {
      const { activeIndex = 0 } = swiper
      const next = filteredTemplates[activeIndex]
      if (!next || next.id === currentTemplate?.id) return
      updateTemplate(next.id)
    },
    [filteredTemplates, updateTemplate, currentTemplate?.id],
  )

  useEffect(() => {
    templatesGallery.current?.slideTo(initialSlide || 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplates])

  return (
    <>
      <TemplateSelectionMobileContainer ref={templateListRef}>
        <TemplatesMobileComponent>
          <ReactSwiper
            onSwiper={swiper => (templatesGallery.current = swiper)}
            initialSlide={initialSlide}
            slidesPerView="auto"
            spaceBetween={0}
            centeredSlides={true}
            slideToClickedSlide={true}
            onActiveIndexChange={handleTemplateChange}
          >
            {filteredTemplates?.map((template: UserDocumentTemplate) => {
              return (
                <SwiperSlide key={template.id}>
                  <TemplateContainer>
                    <TemplatePreviewMobile
                      isActive={currentTemplate?.id === template.id}
                      image={formatImageUrl(template.thumbnail, { width: 154 })}
                    />
                    <TemplateMobile>{template.name}</TemplateMobile>
                  </TemplateContainer>
                </SwiperSlide>
              )
            })}
          </ReactSwiper>
          <BadgeWrapper>
            {!currentTemplate?.premium && <PlanBadges badges={['Free']} />}
            <FormatBadges badges={currentTemplate?.supportedFormats || []} />
          </BadgeWrapper>
        </TemplatesMobileComponent>
      </TemplateSelectionMobileContainer>
    </>
  )
}
